import React from 'react'
import { FileText, FileImage, Paperclip } from 'lucide-react'
import { cn } from '@/lib/utils'

import { API_URL } from '../../../utils'
import { formatBytes } from '../../../utils/formatters'

export interface MeetingFile {
  id: string
  filename: string
  size?: number | null
  mimetype?: string | null
  url?: string | null
}

interface Props {
  file: MeetingFile
  // Trailing control (delete button); omitted in read-only listings.
  action?: React.ReactNode
  className?: string
}

const isImage = (file: MeetingFile) => (file.mimetype || '').startsWith('image/')

// Presigned S3 URL when the query asked for it, otherwise the auth-gated
// download route (works for any file of the caller's organization).
const fileHref = (file: MeetingFile) => file.url || `${API_URL}/download/${file.id}`

const MeetingFileRow: React.FC<Props> = ({ file, action, className }) => {
  const Icon = isImage(file) ? FileImage : file.mimetype === 'application/pdf' ? FileText : Paperclip

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded border px-2 py-1.5 text-sm hover:bg-muted/40',
        className,
      )}
    >
      {isImage(file) ? (
        <img
          src={fileHref(file)}
          alt={file.filename}
          className="h-8 w-8 shrink-0 rounded object-cover bg-muted"
          onError={(e) => {
            e.currentTarget.style.display = 'none'
          }}
        />
      ) : (
        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
      )}

      <a
        href={fileHref(file)}
        target="_blank"
        rel="noopener noreferrer"
        className="flex-1 truncate text-primary hover:underline"
        title={file.filename}
      >
        {file.filename}
      </a>

      {!!file.size && (
        <span className="shrink-0 text-xs text-muted-foreground">{formatBytes(file.size)}</span>
      )}

      {action}
    </div>
  )
}

export default MeetingFileRow
